import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ProductTable from './components/ProductTable';
import TomaSelector from './components/TomaSelector';
import ProgressToast from './components/ProgressToast';
import { useAuth } from './contexts/AuthContext';
import { API_BASE } from './config';
import AdminLayout from './admin/AdminLayout';
import { useAuthRole } from './admin/useAuthRole';

// Sitios conocidos para el filtro pills en columna Catálogo.
const KNOWN_SITES = ['Frecuento', 'Tipti Ecuador', 'Tuti'];

function App() {
  const { currentUser, logout } = useAuth();
  const { role } = useAuthRole();
  const [adminOpen, setAdminOpen] = useState(false);
  const [allTomas, setAllTomas] = useState([]);
  const [selectedToma, setSelectedToma] = useState(null);
  const [tomasLoading, setTomasLoading] = useState(true);
  const [matchingStats, setMatchingStats] = useState(null);
  const [maestroSearch, setMaestroSearch] = useState('');
  const [matcheosSearch, setMatcheosSearch] = useState(''); // Búsqueda para columna Matcheos
  // Ajustes 2604 — ítem 4: lista enriquecida de matches (incluye huérfanos con fallback a
  // catalog_title/product_name desde matching_process). Cuando está presente, ProductTable
  // la renderiza directamente saltándose la búsqueda en la toma.
  const [matcheosOverride, setMatcheosOverride] = useState(null);
  const [catalogoSearch, setCatalogoSearch] = useState(''); // Búsqueda para columna Catálogo
  const [searchExplanation, setSearchExplanation] = useState('');
  const [selectedMaestroProduct, setSelectedMaestroProduct] = useState(null);
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);
  const [maestroProductsToExpand, setMaestroProductsToExpand] = useState(new Set());
  const [matchedIndexes, setMatchedIndexes] = useState([]);
  const [similarityScores, setSimilarityScores] = useState([]);
  const [isMatching, setIsMatching] = useState(false);
  const [maestroWidth, setMaestroWidth] = useState(25); // Default 25%
  const [matcheosWidth, setMatcheosWidth] = useState(25); // Default 25%
  const [isDraggingMaestro, setIsDraggingMaestro] = useState(false);
  const [isDraggingMatcheos, setIsDraggingMatcheos] = useState(false);
  const [matchingLogs, setMatchingLogs] = useState([]);
  const [abortController, setAbortController] = useState(null);
  const [smartQueryDebug, setSmartQueryDebug] = useState(null);
  const [matchingDebug, setMatchingDebug] = useState(null);
  const [maestroCollapsed, setMaestroCollapsed] = useState(false);
  const [catalogoCollapsed, setCatalogoCollapsed] = useState(false);
  const [isDragOverMatcheos, setIsDragOverMatcheos] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, product: null, existingWinner: null });
  const [isAddingMatch, setIsAddingMatch] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportToast, setExportToast] = useState({ open: false, message: '', progress: 0, isComplete: false, totalRows: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadModal, setUploadModal] = useState({ open: false });
  // Filtro por sitio (pills) en columna Catálogo. Default: todos los KNOWN_SITES activos.
  const [siteFilter, setSiteFilter] = useState(() => new Set(KNOWN_SITES));
  // Bulk selection de matches (columna Matcheos). Set<string> de id_catalogo.
  const [selectedMatchIds, setSelectedMatchIds] = useState(() => new Set());
  // Refresh-tick para gatillar re-fetch del maestro tras un PATCH (badges UDM/CONTENIDO).
  const [maestroRefreshTick, setMaestroRefreshTick] = useState(0);
  // Modal genérico de confirmación para bulk delete.
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ open: false, count: 0 });
  // Match counts por id_maestro: { [id]: { count, sitios: [] } } — para columna Maestro.
  const [maestroMatchCounts, setMaestroMatchCounts] = useState({});
  // 2026-06-08: filtro por cantidad de matches en columna Maestro
  const [matchDistribution, setMatchDistribution] = useState(null);
  const [maestroMatchCountFilter, setMaestroMatchCountFilter] = useState(null);

  // Handler para cambiar la búsqueda de matcheos
  const handleMatcheosSearchChange = (newSearch) => {
    setMatcheosSearch(newSearch);
    setSearchExplanation('');
  };

  // PATCH catalog (UDM / CONTENIDO) — usado por InlineEditBadge.
  // El backend marca manually_edited_fields para mostrar override en próximo render.
  const handleCatalogPatch = useCallback(async (idCatalogo, updates) => {
    if (!idCatalogo) throw new Error('id_catalogo missing');
    const response = await fetch(`${API_BASE}/api/catalog/${encodeURIComponent(idCatalogo)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || `HTTP ${response.status}`);
    }
    const data = await response.json();

    // Actualizar in-place el selectedMaestroProduct para que las badges en Matcheos
    // reflejen el cambio inmediatamente.
    if (selectedMaestroProduct && data.row) {
      setSelectedMaestroProduct(prev => {
        if (!prev) return prev;
        // Solo si el patch corresponde al catalog vinculado al producto seleccionado.
        if (String(prev.catalog_link_id) !== String(idCatalogo)) return prev;
        return {
          ...prev,
          catalog_udm: data.row.udm ?? prev.catalog_udm,
          catalog_contenido: data.row.contenido ?? prev.catalog_contenido,
          catalog_manually_edited: data.row.manually_edited_fields ?? prev.catalog_manually_edited,
        };
      });
    }

    // Trigger refresh para repintar la lista maestro entera (otras filas pueden compartir id).
    setMaestroRefreshTick(t => t + 1);
    return data;
  }, [selectedMaestroProduct]);

  // Toggle de pill de sitio (columna Catálogo).
  const handleToggleSiteFilter = useCallback((site) => {
    setSiteFilter(prev => {
      const next = new Set(prev);
      if (next.has(site)) next.delete(site);
      else next.add(site);
      return next;
    });
  }, []);

  // Toggle de checkbox en match (columna Matcheos).
  const handleToggleMatchSelection = useCallback((idCatalogo) => {
    if (!idCatalogo) return;
    setSelectedMatchIds(prev => {
      const next = new Set(prev);
      const key = String(idCatalogo);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleClearMatchSelection = useCallback(() => {
    setSelectedMatchIds(new Set());
  }, []);

  // Abrir modal de bulk delete.
  const handleBulkDeleteMatchesClick = useCallback(() => {
    if (selectedMatchIds.size === 0) return;
    setBulkDeleteModal({ open: true, count: selectedMatchIds.size });
  }, [selectedMatchIds]);

  // Confirmar bulk delete: borra cada match y refresca.
  const handleBulkDeleteConfirm = async () => {
    if (!selectedMaestroProduct) {
      setBulkDeleteModal({ open: false, count: 0 });
      return;
    }
    const ids = Array.from(selectedMatchIds);
    setBulkDeleteModal({ open: false, count: 0 });
    try {
      const results = await Promise.allSettled(
        ids.map(id =>
          fetch(`${API_BASE}/api/matching-process/${selectedMaestroProduct.id}/${encodeURIComponent(id)}`, {
            method: 'DELETE',
          })
        )
      );
      const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok));
      if (failed.length > 0) {
        alert(`Se eliminaron ${ids.length - failed.length} de ${ids.length}. Algunos fallaron.`);
      }
      setSelectedMatchIds(new Set());
      await refreshMatcheos();
    } catch (error) {
      console.error('Error en bulk delete:', error);
      alert('Error al eliminar matches: ' + error.message);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteModal({ open: false, count: 0 });
  };

  // Handler para cambiar la búsqueda de catálogo
  const handleCatalogoSearchChange = (newSearch) => {
    setCatalogoSearch(newSearch);
  };

  useEffect(() => {
    // Obtener todas las tomas y estadísticas al cargar
    setTomasLoading(true);
    fetch(`${API_BASE}/api/tomas`)
      .then(res => res.json())
      .then(data => {
        setAllTomas(data);
        // Seleccionar la última toma (primera en la lista, ya viene ordenada DESC)
        if (data && data.length > 0) {
          setSelectedToma(data[0]);
        }
        setTomasLoading(false);
      })
      .catch(err => {
        console.error('Error al obtener tomas:', err);
        setTomasLoading(false);
      });

    fetch(`${API_BASE}/api/matching-stats`)
      .then(res => res.json())
      .then(data => setMatchingStats(data))
      .catch(err => console.error('Error al obtener estadísticas:', err));

    // Cargar match counts globales (todos los maestros con matches) — usado por columna Maestro.
    fetch(`${API_BASE}/api/maestro/match-counts`)
      .then(res => res.json())
      .then(data => setMaestroMatchCounts(data.counts || {}))
      .catch(err => console.error('Error al obtener match counts:', err));

    // 2026-06-08: distribución de matches para el dropdown filtro
    fetch(`${API_BASE}/api/maestro/match-distribution`)
      .then(res => res.json())
      .then(data => setMatchDistribution(data.distribution || null))
      .catch(err => console.error('Error al obtener match distribution:', err));
  }, []);

  // Helper para refrescar match counts (después de agregar/eliminar matches).
  const refreshMatchCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/maestro/match-counts`);
      const data = await res.json();
      setMaestroMatchCounts(data.counts || {});
    } catch (e) {
      console.error('Error al refrescar match counts:', e);
    }
  }, []);

  const handleAISearch = async (productName, productCode) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productName, productCode, debug: true })
      });

      const data = await response.json();
      // AI Search busca en el catálogo (no en matcheos)
      setCatalogoSearch(data.query);
      setSearchExplanation(data.explanation);
      setSmartQueryDebug(data.debugInfo);

      // Agregar log de smart query al panel de debug
      if (data.debugInfo) {
        addLog('Smart Query AI generada', {
          query: data.query,
          explanation: data.explanation
        });
      }
    } catch (error) {
      console.error('Error al generar búsqueda AI:', error);
    }
  };

  const handleSelectCatalogProduct = async (product) => {
    try {
      // Si el producto ya está seleccionado, deseleccionarlo
      if (selectedCatalogProduct?.id_catalogo === product.id_catalogo) {
        setSelectedCatalogProduct(null);
        setMaestroSearch(''); // Limpiar búsqueda de maestro
        setMaestroProductsToExpand(new Set()); // Limpiar productos a expandir
        return;
      }

      // Deseleccionar producto de maestro si hay alguno seleccionado
      setSelectedMaestroProduct(null);

      // Seleccionar el producto de catálogo
      setSelectedCatalogProduct(product);

      // Buscar productos maestro relacionados con este producto de catálogo
      const response = await fetch(`${API_BASE}/api/catalog/${product.id_catalogo}/maestro`);
      const data = await response.json();

      console.log(`📦 Productos maestro relacionados:`, data.data);

      if (data.data && data.data.length > 0) {
        // Construir un query string con los códigos de producto usando el prefijo OR:
        const productCodes = data.data.map(p => `OR:${p.cod_producto}`).join(' ');
        setMaestroSearch(productCodes);

        // Marcar estos productos para que se expandan automáticamente
        const codProductosToExpand = new Set(data.data.map(p => p.cod_producto));
        setMaestroProductsToExpand(codProductosToExpand);
      }
    } catch (error) {
      console.error('Error al buscar productos maestro relacionados:', error);
    }
  };

  const handleSelectMaestroProduct = async (product) => {
    // Si el producto ya está seleccionado, deseleccionarlo
    if (selectedMaestroProduct?.cod_producto === product.cod_producto) {
      setSelectedMaestroProduct(null);
      setMatchedIndexes([]); // Limpiar matches
      setSimilarityScores([]); // Limpiar scores
      setMatchingLogs([]); // Limpiar logs
      setSmartQueryDebug(null); // Limpiar debug
      setMatchingDebug(null); // Limpiar matching debug
      setMatcheosSearch(''); // Limpiar búsqueda de matcheos
      // setCatalogoSearch NO se limpia - mantiene su búsqueda
      setSearchExplanation(''); // Limpiar explicación
      setIsMatching(false);
      setSelectedMatchIds(new Set()); // Limpiar bulk selection
      return;
    }

    // Deseleccionar producto de catálogo si hay alguno seleccionado
    setSelectedCatalogProduct(null);
    setSelectedMatchIds(new Set()); // Limpiar bulk selection al cambiar maestro

    // Si es un producto diferente, seleccionarlo
    setSelectedMaestroProduct(product);

    // Limpiar SOLO la columna Matcheos (Catálogo NO se toca)
    setMatcheosSearch(''); // Columna Matcheos empieza vacía
    setMatcheosOverride(null); // reset override de matcheos (Ajustes 2604 — ítem 4)
    // setCatalogoSearch NO se limpia - el usuario puede seguir buscando
    setSearchExplanation('');
    setMatchedIndexes([]); // Limpiar matches anteriores
    setSimilarityScores([]); // Limpiar scores anteriores
    setMatchingLogs([]); // Limpiar logs anteriores
    setSmartQueryDebug(null); // Limpiar debug anterior
    setMatchingDebug(null); // Limpiar matching debug anterior
    setIsMatching(true); // Mostrar indicador de loading en columna Matcheos

    // Pequeño delay para que se vea el cambio
    await new Promise(resolve => setTimeout(resolve, 100));

    // Verificar si ya tiene productos relacionados en matching_process
    // Esto cargará los datos SOLO para la columna MATCHEOS
    try {
      console.log('🔍 Consultando matches para producto maestro:', { id: product.id, nombre: product.nombre });
      const response = await fetch(`${API_BASE}/api/maestro/${product.id}/catalog`);
      const data = await response.json();
      console.log('📊 Respuesta del servidor:', data);

      if (data.data && data.data.length > 0) {
        const siteWinners = data.data.filter(p => Number(p.site_winner) === 1);
        const orphans = siteWinners.filter(p => p.is_orphan).length;

        console.log(`✓ Producto maestro tiene ${data.data.length} productos relacionados en matching_process (${siteWinners.length} site winners, ${orphans} huérfanos)`);

        if (siteWinners.length > 0) {
          // Ajustes 2604 — ítem 4: usamos override directo (con huérfanos incluidos) en
          // vez de buscar en la toma, así los matches que apuntan a tomas viejas se ven.
          setMatcheosOverride(siteWinners);
          setMatcheosSearch('');
          const orphanNote = orphans > 0 ? ` (${orphans} de toma anterior)` : '';
          setSearchExplanation(`${siteWinners.length} match${siteWinners.length !== 1 ? 'es' : ''} encontrado${siteWinners.length !== 1 ? 's' : ''}${orphanNote}`);
          setIsMatching(false);
        } else {
          console.log('ℹ Producto maestro tiene matches pero ninguno es site winner');
          setMatcheosOverride([]);
          setMatcheosSearch('');
          setSearchExplanation('');
          setIsMatching(false);
        }
      } else {
        console.log('ℹ Producto maestro NO tiene productos relacionados');
        setMatcheosOverride(null);
        setMatcheosSearch('');
        setSearchExplanation('');
        setIsMatching(false);
      }
    } catch (error) {
      console.error('Error al verificar productos relacionados:', error);
      setMatcheosOverride(null);
      setMatcheosSearch('');
      setSearchExplanation('');
      setIsMatching(false);
    }
  };

  const addLog = (message, data = null, debugInfo = null) => {
    const logEntry = {
      timestamp: new Date().toLocaleTimeString(),
      message,
      data: data ? JSON.stringify(data, null, 2) : null,
      debugInfo: debugInfo
    };
    setMatchingLogs(prev => [...prev, logEntry]);
  };

  const cancelMatching = () => {
    if (abortController) {
      abortController.abort();
      addLog('Búsqueda cancelada por el usuario');
      setIsMatching(false);
      setAbortController(null);
    }
  };

  const handleAIMatch = async (catalogProducts) => {
    if (!selectedMaestroProduct || catalogProducts.length === 0) {
      setMatchedIndexes([]);
      setSimilarityScores([]);
      setMatchingLogs([]);
      setMatchingDebug(null);
      setIsMatching(false);
      return;
    }

    // Resetear estado anterior y empezar nuevo matching
    setMatchedIndexes([]);
    setSimilarityScores([]);
    setMatchingLogs([]);
    setMatchingDebug(null);
    setIsMatching(true);

    // Limitar a 100 productos máximo para evitar error 413 Payload Too Large
    const MAX_PRODUCTS = 100;
    const limitedProducts = catalogProducts.slice(0, MAX_PRODUCTS);
    const wasLimited = catalogProducts.length > MAX_PRODUCTS;

    addLog('Iniciando proceso de matching', {
      producto: selectedMaestroProduct.nombre,
      catalogoSize: catalogProducts.length,
      limitedTo: wasLimited ? MAX_PRODUCTS : catalogProducts.length,
      warning: wasLimited ? `⚠️ Catálogo limitado a ${MAX_PRODUCTS} productos (había ${catalogProducts.length}). Refina tu búsqueda para mejores resultados.` : null
    });

    // Crear nuevo AbortController
    const controller = new AbortController();
    setAbortController(controller);

    try {
      // Preparar el debugInfo ANTES de enviar (replicando el prompt exacto del servidor)
      // Estructura optimizada para cache: parte estática primero, dinámica al final
      const systemPrompt = "Eres un experto en product matching para retail. Tu especialidad es identificar cuando dos productos con nombres diferentes son EXACTAMENTE el mismo producto, siendo flexible con descriptores genéricos pero estricto con atributos clave (tamaño, variante, modelo).";

      const userPrompt = `Eres un experto en matching de productos de retail/ecommerce. Trabajas con productos de SUPERMERCADO, ROPA y ELECTRÓNICA.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TAREA: Encuentra cuáles productos del catálogo son EXACTAMENTE EL MISMO PRODUCTO que el maestro.

📋 REGLAS DE MATCHING:

✓ ES MATCH cuando:
  • Misma MARCA + mismo TAMAÑO/CAPACIDAD/MODELO
  • Diferentes formas de escribir el mismo producto:
    - "Cola Pepsi 3L" = "Cola Pepsi 3000ml" = "Pepsi 3 Litros"
    - "Camisa Polo M" = "Camisa Polo Talla M" = "Polo M"
    - "iPhone 14 128GB" = "Apple iPhone 14 de 128GB"
  • Ignora descriptores genéricos:
    - "Bebida Gaseosa Pepsi 3L" = "Cola Pepsi 3L"
    - "Gaseosa Sabor Cola Pepsi 3000ml" = "Pepsi 3L"
    - "Pantalón Jean Levis 32" = "Jean Levis Talla 32"

✗ NO ES MATCH cuando:
  • TAMAÑO/CAPACIDAD diferente:
    - "Pepsi 3L" ≠ "Pepsi 2L"
    - "iPhone 128GB" ≠ "iPhone 256GB"
  • VARIANTE diferente (aunque mismo tamaño):
    - "Pepsi 3L" ≠ "Pepsi Light 3L"
    - "Pepsi 3L" ≠ "Pepsi Zero 3L"
    - "Pepsi 3L" ≠ "Pepsi Sin Azúcar 3L"
  • MODELO diferente:
    - "iPhone 14" ≠ "iPhone 14 Pro"
    - "Samsung S23" ≠ "Samsung S23 Ultra"
  • COLOR diferente (en ropa/electrónica):
    - "Camisa Roja M" ≠ "Camisa Azul M"

⚡ CASOS ESPECIALES:

1. BEBIDAS/ALIMENTOS:
   - "Pepsi" regular ≠ "Pepsi Light/Zero/Sin Azúcar"
   - Solo marca + tamaño debe coincidir
   - Ignora: "Bebida", "Gaseosa", "Sabor", "Refresco", etc.

2. ROPA:
   - Marca + tipo + talla deben coincidir
   - Color DEBE coincidir si está mencionado en maestro
   - Ignora: "Prenda", "Ropa", "Para hombre/mujer", etc.

3. ELECTRÓNICA:
   - Marca + modelo + capacidad deben coincidir EXACTAMENTE
   - "Pro", "Max", "Plus" son modelos DIFERENTES
   - Color puede ignorarse si no está en maestro

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FORMATO DE RESPUESTA:
{
  "matches": [array de índices 0-based de matches PERFECTOS encontrados],
  "scores": [
    {"index": 0, "score": 95, "reason": "Misma marca y tamaño, solo difiere en descriptor"},
    {"index": 1, "score": 85, "reason": "Misma marca, tamaño similar"},
    ...
  ],
  "explanation": "razón breve de los matches perfectos"
}

📊 SCORING (0-100%):
- 100% = Match PERFECTO (mismo producto, solo difiere en redacción)
- 90-99% = Match muy probable (descriptor genérico diferente)
- 80-89% = Similar (misma marca, tamaño cercano o descriptor ligeramente diferente)
- 70-79% = Parecido (misma marca, algunas diferencias menores)
- 60-69% = Algo similar (misma marca, diferentes atributos)
- <60% = Poco similar

⚠️ IMPORTANTE:
- Devuelve scores para TODOS los productos del catálogo
- "matches" solo incluye productos con score >= 95% (match perfecto)
- Sé FLEXIBLE con nombres pero ESTRICTO con atributos clave
- Ordena "scores" de mayor a menor score

Responde SOLO el JSON, nada más.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📦 DATOS A ANALIZAR (parte dinámica):

PRODUCTO MAESTRO (a buscar):
"${selectedMaestroProduct.nombre}"

CATÁLOGO (${limitedProducts.length} productos${wasLimited ? ` de ${catalogProducts.length}` : ''}):
${limitedProducts.map((p, i) => `[${i}] "${p.title}"`).join('\n')}`;

      addLog('Enviando solicitud a OpenAI...', null, {
        systemPrompt: systemPrompt,
        userPrompt: userPrompt,
        maestroProduct: selectedMaestroProduct.nombre,
        catalogSize: limitedProducts.length
      });

      const startTime = Date.now();

      // Crear timeout de 65 segundos (un poco más que el del servidor)
      const timeoutId = setTimeout(() => controller.abort(), 65000);

      const response = await fetch(`${API_BASE}/api/ai-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          maestroProduct: selectedMaestroProduct,
          catalogProducts: limitedProducts
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const elapsed = Date.now() - startTime;
      addLog(`Respuesta recibida en ${elapsed}ms`);

      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      addLog('Matching completado', {
        matchesFound: data.matches?.length || 0,
        scoresCalculated: data.scores?.length || 0
      });

      setMatchedIndexes(data.matches || []);
      setSimilarityScores(data.scores || []);
      setMatchingDebug(data.debugInfo || null);
    } catch (error) {
      if (error.name === 'AbortError') {
        addLog('Búsqueda cancelada o excedió tiempo de espera (65s)');
      } else {
        addLog('ERROR', { error: error.message });
        console.error('Error al hacer matching AI:', error);
      }
      setMatchedIndexes([]);
      setSimilarityScores([]);
    } finally {
      setIsMatching(false);
      setAbortController(null);
    }
  };

  const handleMouseDownMaestro = () => {
    setIsDraggingMaestro(true);
  };

  const handleMouseDownMatcheos = () => {
    setIsDraggingMatcheos(true);
  };

  // Handlers para drag and drop de productos del catálogo a matcheos
  const handleDragOverMatcheos = (e) => {
    e.preventDefault();
    setIsDragOverMatcheos(true);
  };

  const handleDragLeaveMatcheos = (e) => {
    e.preventDefault();
    setIsDragOverMatcheos(false);
  };

  const handleDropOnMatcheos = async (e) => {
    e.preventDefault();
    setIsDragOverMatcheos(false);

    if (!selectedMaestroProduct) {
      alert('Primero selecciona un producto maestro');
      return;
    }

    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (!jsonData) {
        console.warn('No hay datos en el drop');
        return;
      }

      const data = JSON.parse(jsonData);
      console.log('📦 Producto arrastrado:', data);

      if (!data.id_catalogo || !data.sitio) {
        console.error('Datos incompletos:', data);
        alert('Datos del producto incompletos');
        return;
      }

      // Mostrar feedback inmediato
      setIsAddingMatch(true);

      // Crear AbortController para timeout de 30 segundos (Cloud Functions pueden ser lentas)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        // Verificar si ya existe un site winner del mismo sitio
        const checkResponse = await fetch(
          `${API_BASE}/api/matching-process/check-site-winner?id_maestro=${selectedMaestroProduct.id}&sitio=${encodeURIComponent(data.sitio)}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!checkResponse.ok) {
          throw new Error(`Error del servidor: ${checkResponse.status}`);
        }

        const checkData = await checkResponse.json();

        if (checkData.exists) {
          // Ya existe un site winner del mismo sitio, mostrar modal de confirmación
          setIsAddingMatch(false);
          setConfirmModal({
            open: true,
            product: data,
            existingWinner: checkData.siteWinner
          });
        } else {
          // No hay conflicto, agregar directamente
          await addMatchToMaestro(data);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Tiempo de espera agotado. Intenta de nuevo.');
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('Error al procesar drop:', error);
      setIsAddingMatch(false);
      alert('Error al procesar el producto: ' + error.message);
    }
  };

  // Handler reutilizable: dispara el mismo flow del drag&drop. Usado por botón "+ Agregar al Match"
  // en columna Toma. Recibe el producto crudo (id_catalogo, sitio, title, imagen).
  const handleAddMatchClick = async (product) => {
    if (!selectedMaestroProduct) {
      alert('Primero selecciona un producto maestro');
      return;
    }
    if (!product || !product.id_catalogo || !product.sitio) {
      console.error('Datos incompletos para agregar match:', product);
      alert('Datos del producto incompletos');
      return;
    }
    try {
      setIsAddingMatch(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        const checkResponse = await fetch(
          `${API_BASE}/api/matching-process/check-site-winner?id_maestro=${selectedMaestroProduct.id}&sitio=${encodeURIComponent(product.sitio)}`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        if (!checkResponse.ok) throw new Error(`Error del servidor: ${checkResponse.status}`);
        const checkData = await checkResponse.json();
        if (checkData.exists) {
          setIsAddingMatch(false);
          setConfirmModal({
            open: true,
            product,
            existingWinner: checkData.siteWinner,
          });
        } else {
          await addMatchToMaestro(product);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') throw new Error('Tiempo de espera agotado. Intenta de nuevo.');
        throw fetchError;
      }
    } catch (error) {
      console.error('Error al agregar match desde botón:', error);
      setIsAddingMatch(false);
      alert('Error al agregar el match: ' + error.message);
    }
  };

  const addMatchToMaestro = async (catalogProduct) => {
    try {
      setIsAddingMatch(true);

      // Crear AbortController para timeout de 30 segundos (Cloud Functions pueden ser lentas)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${API_BASE}/api/matching-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_maestro: selectedMaestroProduct.id,
          id_catalogo: catalogProduct.id_catalogo,
          sitio: catalogProduct.sitio,
          match_level: 100,
          product_name: selectedMaestroProduct.nombre || '',
          catalog_title: catalogProduct.title || ''
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 409) {
        const errorData = await response.json().catch(() => null);
        setIsAddingMatch(false);
        // Mostrar modal de reemplazo reutilizando confirmModal existente
        setConfirmModal({
          open: true,
          product: catalogProduct,
          existingWinner: errorData?.existing || null
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Error del servidor: ${response.status}`);
      }

      console.log('✅ Match agregado exitosamente');

      // Refrescar la columna de matcheos
      await refreshMatcheos();
      setIsAddingMatch(false);
    } catch (error) {
      console.error('Error al agregar match:', error);
      setIsAddingMatch(false);
      if (error.name === 'AbortError') {
        alert('Tiempo de espera agotado. Intenta de nuevo.');
      } else {
        alert('Error al agregar el match: ' + error.message);
      }
    }
  };

  const handleConfirmReplace = async () => {
    const { product, existingWinner } = confirmModal;

    try {
      setIsAddingMatch(true);

      // Crear AbortController para timeout de 30 segundos (Cloud Functions pueden ser lentas)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Primero eliminar el site winner existente
      const deleteResponse = await fetch(
        `${API_BASE}/api/matching-process/${selectedMaestroProduct.id}/${existingWinner.id_catalogo}`,
        { method: 'DELETE', signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!deleteResponse.ok) {
        throw new Error('Error al eliminar el match anterior');
      }

      console.log('🗑️ Site winner anterior eliminado');

      // Luego agregar el nuevo
      await addMatchToMaestro(product);

      setConfirmModal({ open: false, product: null, existingWinner: null });
    } catch (error) {
      console.error('Error al reemplazar site winner:', error);
      setIsAddingMatch(false);
      if (error.name === 'AbortError') {
        alert('Tiempo de espera agotado. Intenta de nuevo.');
      } else {
        alert('Error al reemplazar el site winner: ' + error.message);
      }
    }
  };

  const handleCancelReplace = () => {
    setConfirmModal({ open: false, product: null, existingWinner: null });
  };

  const handleUploadMaestroClick = () => {
    setUploadModal({ open: true });
  };

  const handleUploadMaestroConfirm = () => {
    setUploadModal({ open: false });
    // Trigger file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = handleFileSelected;
    input.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64 = event.target.result.split(',')[1];

          // 2026-06-08: NO usar FUNCTIONS_DIRECT_URL (Cloud Functions deprecated).
          // Backend único en price (nginx :443 → :3101). En dev API_BASE='http://localhost:3101',
          // en prod API_BASE='' (relativo, mismo host).
          const response = await fetch(`${API_BASE}/api/upload-maestro`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileData: base64 })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.details || data.error || 'Error al subir archivo');
          }

          alert(`Maestro actualizado exitosamente.\n\nNuevos: ${data.rowsInserted}\nActualizados: ${data.rowsUpdated}\nEliminados: ${data.rowsDeleted}\nTiempo: ${(data.timeMs / 1000).toFixed(1)}s\nBackup: ${data.backupTable}`);

          // Refresh matching stats
          const statsResponse = await fetch(`${API_BASE}/api/matching-stats`);
          const statsData = await statsResponse.json();
          setMatchingStats(statsData);

          // Clear current selection since maestro changed
          setSelectedMaestroProduct(null);
          setMaestroSearch('');
          setMatcheosSearch('');
          setCatalogoSearch('');
        } catch (error) {
          console.error('Error uploading:', error);
          alert(`Error al actualizar maestro: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      alert(`Error al leer archivo: ${error.message}`);
      setIsUploading(false);
    }
  };

  const handleExportMatches = async () => {
    // 2026-06-08: backend unificado en price (Express :3101 detrás de nginx).
    // API_BASE='' en prod → URL relativa → nginx proxy. Cloud Functions deprecated.
    // 2026-06-17 "exportador inteligente": si hay toma seleccionada, pasamos asOfToma=<id>
    // para que el Excel muestre, por cada match, el precio que tuvo COMO MÁXIMO a la fecha
    // de esa toma (nunca precios de tomas posteriores). Sin toma → export default.
    const asOfParam = selectedToma?.id != null ? `?asOfToma=${selectedToma.id}` : '';
    const exportUrl = `${API_BASE}/api/export-maestro-matches${asOfParam}`;
    const countUrl = `${API_BASE}/api/export-maestro-matches/count`;

    // PROGRESO ASINTÓTICO (2026-06-08): elegido sobre fases con tiempos fijos porque el
    // backend tarda variable (5-50s según volumen). Fórmula `90 - 90*exp(-t/T)` con T=8s
    // garantiza movimiento continuo mientras el fetch está in-flight, sin alcanzar nunca
    // 100% hasta que el response real llega. En 10s ~70%, en 20s ~84%, en 40s ~89%.
    // El response dispara jump a 100%. Mensaje cambia según rango para dar contexto.
    let progressInterval = null;
    const startTime = Date.now();
    const stopProgress = () => {
      if (progressInterval) { clearInterval(progressInterval); progressInterval = null; }
    };
    const startAsymptoticProgress = (totalRows) => {
      stopProgress();
      const T = 8000; // constante de tiempo en ms
      progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const p = 90 - 90 * Math.exp(-elapsed / T);
        let msg;
        if (p < 20) msg = 'Consultando matches...';
        else if (p < 50) msg = totalRows > 0
          ? `Generando archivo XLSX (${totalRows.toLocaleString('es-AR')} filas)...`
          : 'Generando archivo XLSX...';
        else msg = 'Casi listo...';
        setExportToast(prev => ({ ...prev, progress: p, message: msg, totalRows }));
      }, 200);
    };

    try {
      setIsExporting(true);
      setExportToast({ open: true, message: 'Consultando matches...', progress: 1, isComplete: false, totalRows: 0 });

      // 1) Arrancamos la animación asintótica YA — count y fetch corren con progreso visible
      startAsymptoticProgress(0);

      // 2) Contar filas (rápido, ~1s) — actualiza totalRows en el toast cuando llega
      let totalRows = 0;
      try {
        const countRes = await fetch(countUrl);
        if (countRes.ok) {
          const j = await countRes.json();
          totalRows = Number(j.total) || 0;
          // Reiniciar interval con totalRows real para que aparezca en el mensaje
          startAsymptoticProgress(totalRows);
        }
      } catch (_) { /* no bloquea */ }

      // 3) Iniciar download — fase pesada (5-50s). El interval sigue moviendo la barra.
      const response = await fetch(exportUrl);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMsg = errorData?.details || errorData?.error || 'Error al exportar';
        throw new Error(errorMsg);
      }

      // Response OK — paramos la asintótica y saltamos a 95% mientras se baja el blob
      stopProgress();
      setExportToast(prev => ({ ...prev, message: 'Descargando archivo...', progress: 95, totalRows }));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'match_export.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportToast(prev => ({ ...prev, message: '¡Listo!', progress: 100, isComplete: true, totalRows }));
    } catch (error) {
      stopProgress();
      console.error('Error al exportar:', error);
      setExportToast({ open: false, message: '', progress: 0, isComplete: false, totalRows: 0 });
      alert(`Error al exportar los matches: ${error.message}`);
    } finally {
      stopProgress();
      setIsExporting(false);
    }
  };

  const refreshMatcheos = async () => {
    if (!selectedMaestroProduct) return;

    try {
      const response = await fetch(`${API_BASE}/api/maestro/${selectedMaestroProduct.id}/catalog`);
      const data = await response.json();

      if (data.data && data.data.length > 0) {
        const siteWinners = data.data.filter(p => Number(p.site_winner) === 1);
        const orphans = siteWinners.filter(p => p.is_orphan).length;
        if (siteWinners.length > 0) {
          setMatcheosOverride(siteWinners);
          setMatcheosSearch('');
          const orphanNote = orphans > 0 ? ` (${orphans} de toma anterior)` : '';
          setSearchExplanation(`${siteWinners.length} match${siteWinners.length !== 1 ? 'es' : ''} encontrado${siteWinners.length !== 1 ? 's' : ''}${orphanNote}`);
        } else {
          setMatcheosOverride([]);
          setMatcheosSearch('');
          setSearchExplanation('');
        }
      } else {
        setMatcheosOverride(null);
        setMatcheosSearch('');
        setSearchExplanation('');
      }

      // Refrescar estadísticas globales
      const statsResponse = await fetch(`${API_BASE}/api/matching-stats`);
      const statsData = await statsResponse.json();
      setMatchingStats(statsData);

      // Refrescar match counts para repintar números de la columna Maestro.
      await refreshMatchCounts();
    } catch (error) {
      console.error('Error al refrescar matcheos:', error);
    }
  };

  const handleDeleteMatch = async (id_catalogo) => {
    if (!selectedMaestroProduct) return;

    if (!window.confirm('¿Estás seguro de que deseas eliminar este match?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/matching-process/${selectedMaestroProduct.id}/${id_catalogo}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Error al eliminar match');
      }

      console.log(`🗑️ Match eliminado: catálogo=${id_catalogo}`);

      // Refrescar la columna de matcheos
      await refreshMatcheos();
    } catch (error) {
      console.error('Error al eliminar match:', error);
      alert('Error al eliminar el match');
    }
  };

  const handleMouseMove = (e) => {
    const container = document.querySelector('.dashboard-container');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;
    const containerWidth = containerRect.width;
    const newWidthPercent = (mouseX / containerWidth) * 100;

    if (isDraggingMaestro) {
      // Limitar entre 15% y 50%
      if (newWidthPercent >= 15 && newWidthPercent <= 50) {
        setMaestroWidth(newWidthPercent);
      }
    } else if (isDraggingMatcheos) {
      // Calcular el ancho de matcheos basado en la posición del mouse
      // mouseX debe estar entre el final de maestro y antes del catálogo
      const maestroPixels = (maestroWidth / 100) * containerWidth;
      const matcheosPixels = mouseX - maestroPixels;
      const matcheosPercent = (matcheosPixels / containerWidth) * 100;

      // Limitar entre 15% y 50%
      if (matcheosPercent >= 15 && matcheosPercent <= 50) {
        setMatcheosWidth(matcheosPercent);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDraggingMaestro(false);
    setIsDraggingMatcheos(false);
  };

  useEffect(() => {
    if (isDraggingMaestro || isDraggingMatcheos) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line
  }, [isDraggingMaestro, isDraggingMatcheos, maestroWidth, matcheosWidth]);

  // Si el usuario abrió el admin Y tiene rol admin+, renderizar el shell admin.
  // No-op si no tiene permisos: AdminLayout muestra pantalla "sin permisos".
  if (adminOpen) {
    return <AdminLayout onExit={() => setAdminOpen(false)} />;
  }

  const isAdminLike = role === 'admin' || role === 'superadmin';

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <img src="/Images/Pulpou.svg" alt="Pulpou Logo" className="logo logo-pulpou" />
          <img src="/Images/Tia_Supermarket-Logo.svg.png" alt="Tia Logo" className="logo logo-tia" />
        </div>
        <div className="header-right">
          <div className="header-title-row">
            <h1>Catalog Matcher</h1>
            <div className="user-info">
              <button
                className="upload-btn"
                onClick={handleUploadMaestroClick}
                disabled={isUploading}
                title="Actualizar maestro desde archivo Excel"
              >
                {isUploading ? 'Subiendo...' : 'Actualizar Maestro'}
              </button>
              <button
                className="export-btn"
                onClick={handleExportMatches}
                disabled={isExporting}
                title="Exportar maestro con matches a Excel"
              >
                {isExporting ? 'Exportando...' : 'Exportar Matches'}
              </button>
              {isAdminLike && (
                <button
                  className="admin-btn"
                  onClick={() => setAdminOpen(true)}
                  title="Abrir panel de administración"
                >
                  Admin
                </button>
              )}
              <span className="user-email">{currentUser?.email}</span>
              <button className="logout-btn" onClick={logout}>Salir</button>
            </div>
          </div>
          <div className="header-info">
            <TomaSelector
              tomas={allTomas}
              selectedToma={selectedToma}
              onTomaChange={setSelectedToma}
              loading={tomasLoading}
            />
            {matchingStats && (
              <p className="matching-stats">
                Productos maestro con match: <strong>{matchingStats.withMatches} de {matchingStats.total}</strong> ({matchingStats.percentage}%)
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {/* Columna 1: Maestro */}
        <div className={`table-section ${maestroCollapsed ? 'collapsed' : ''}`} style={{ width: maestroCollapsed ? '40px' : `${maestroWidth}%` }}>
          <div className="column-header">
            <h2>Maestro de Productos</h2>
            <button
              className="collapse-btn"
              onClick={() => setMaestroCollapsed(!maestroCollapsed)}
              title={maestroCollapsed ? "Expandir" : "Colapsar"}
            >
              {maestroCollapsed ? '▶' : '◀'}
            </button>
          </div>
          {!maestroCollapsed && (
            <ProductTable
              type="maestro"
              searchTerm={maestroSearch}
              onSearchChange={setMaestroSearch}
              tomaTable={null}
              onAISearch={handleAISearch}
              onSelectProduct={handleSelectMaestroProduct}
              selectedProduct={selectedMaestroProduct}
              productsToExpand={maestroProductsToExpand}
              onCatalogPatch={handleCatalogPatch}
              refreshTick={maestroRefreshTick}
              matchCounts={maestroMatchCounts}
              matchCountFilter={maestroMatchCountFilter}
              onMatchCountFilterChange={setMaestroMatchCountFilter}
              matchDistribution={matchDistribution}
            />
          )}
        </div>

        {/* Drag handle entre Maestro y Matcheos */}
        <div className="drag-handle" onMouseDown={handleMouseDownMaestro}>
          <div className="drag-handle-icon">
            <div className="drag-handle-dot"></div>
            <div className="drag-handle-dot"></div>
            <div className="drag-handle-dot"></div>
            <div className="drag-handle-dot"></div>
            <div className="drag-handle-dot"></div>
            <div className="drag-handle-dot"></div>
          </div>
        </div>

        {/* Columna 2: Matcheos (solo visible si hay producto maestro seleccionado) */}
        {selectedMaestroProduct && (
          <>
            <div className="table-section matcheos-section" style={{
              width: catalogoCollapsed
                ? `calc(100% - ${maestroCollapsed ? '40px' : maestroWidth + '%'} - 40px)`
                : `${matcheosWidth}%`
            }}>
              <div className="column-header">
                <h2>Matches</h2>
              </div>
              {selectedToma ? (
                <ProductTable
                  type="matcheos"
                  overrideProducts={matcheosOverride}
                  searchTerm={matcheosSearch}
                  onSearchChange={handleMatcheosSearchChange}
                  tomaTable={`toma_${selectedToma.id}_${selectedToma.cliente_id}`}
                  onAIMatch={handleAIMatch}
                  matchedIndexes={matchedIndexes}
                  similarityScores={similarityScores}
                  isMatching={isMatching || isAddingMatch}
                  matchingProduct={selectedMaestroProduct}
                  matchingLogs={matchingLogs}
                  onCancelMatching={cancelMatching}
                  smartQueryDebug={smartQueryDebug}
                  matchingDebug={matchingDebug}
                  onSelectProduct={null}
                  selectedProduct={null}
                  showOnlyMatched={true}
                  isDragOver={isDragOverMatcheos}
                  onDragOver={handleDragOverMatcheos}
                  onDragLeave={handleDragLeaveMatcheos}
                  onDrop={handleDropOnMatcheos}
                  onDeleteMatch={handleDeleteMatch}
                  isAddingMatch={isAddingMatch}
                  maestroBadges={selectedMaestroProduct ? {
                    udm: selectedMaestroProduct.catalog_udm,
                    contenido: selectedMaestroProduct.catalog_contenido,
                    manually_edited_fields: Array.isArray(selectedMaestroProduct.catalog_manually_edited)
                      ? selectedMaestroProduct.catalog_manually_edited
                      : (typeof selectedMaestroProduct.catalog_manually_edited === 'string'
                          ? (() => { try { const p = JSON.parse(selectedMaestroProduct.catalog_manually_edited); return Array.isArray(p) ? p : []; } catch { return []; } })()
                          : []),
                  } : null}
                  maestroFull={selectedMaestroProduct}
                  maestroMatchCount={selectedMaestroProduct ? (maestroMatchCounts[String(selectedMaestroProduct.id)]?.count || 0) : 0}
                  onCatalogPatch={handleCatalogPatch}
                  selectedMatchIds={selectedMatchIds}
                  onToggleMatchSelection={handleToggleMatchSelection}
                  onBulkDeleteMatches={handleBulkDeleteMatchesClick}
                  onClearMatchSelection={handleClearMatchSelection}
                />
              ) : (
                <p className="loading">Cargando última toma...</p>
              )}
            </div>

            {/* Drag handle entre Matcheos y Catálogo */}
            <div className="drag-handle" onMouseDown={handleMouseDownMatcheos}>
              <div className="drag-handle-icon">
                <div className="drag-handle-dot"></div>
                <div className="drag-handle-dot"></div>
                <div className="drag-handle-dot"></div>
                <div className="drag-handle-dot"></div>
                <div className="drag-handle-dot"></div>
                <div className="drag-handle-dot"></div>
              </div>
            </div>
          </>
        )}

        {/* Columna 3: Catálogo (ajusta su ancho según si Matcheos está visible) */}
        <div
          className={`table-section ${catalogoCollapsed ? 'collapsed' : ''}`}
          style={{
            width: catalogoCollapsed
              ? '40px'
              : selectedMaestroProduct
                ? `calc(100% - ${maestroCollapsed ? '40px' : maestroWidth + '%'} - ${matcheosWidth}% - ${catalogoCollapsed ? '0px' : '0px'})`
                : `calc(100% - ${maestroCollapsed ? '40px' : maestroWidth + '%'})`
          }}
        >
          <div className="column-header">
            <h2>
              {selectedToma
                ? `Toma ${selectedToma.id} - ${new Date(selectedToma.fecha_fin).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}`
                : 'Catálogo / Toma'
              }
            </h2>
            <button
              className="collapse-btn"
              onClick={() => setCatalogoCollapsed(!catalogoCollapsed)}
              title={catalogoCollapsed ? "Expandir" : "Colapsar"}
            >
              {catalogoCollapsed ? '◀' : '▶'}
            </button>
          </div>
          {!catalogoCollapsed && selectedToma ? (
            <ProductTable
              type="toma"
              searchTerm={catalogoSearch}
              onSearchChange={handleCatalogoSearchChange}
              tomaTable={`toma_${selectedToma.id}_${selectedToma.cliente_id}`}
              onAIMatch={null}
              matchedIndexes={[]}
              similarityScores={[]}
              isMatching={false}
              matchingProduct={null}
              matchingLogs={[]}
              onCancelMatching={null}
              smartQueryDebug={null}
              matchingDebug={null}
              onSelectProduct={null}
              selectedProduct={null}
              showOnlyMatched={false}
              excludeMatched={!!selectedMaestroProduct}
              availableSites={KNOWN_SITES}
              siteFilter={siteFilter}
              onToggleSiteFilter={handleToggleSiteFilter}
              onAddMatchClick={selectedMaestroProduct ? handleAddMatchClick : null}
              onCatalogPatch={handleCatalogPatch}
              noMaestroSelected={!selectedMaestroProduct}
            />
          ) : !catalogoCollapsed ? (
            <p className="loading">Cargando última toma...</p>
          ) : null}
        </div>
      </div>

      {/* Modal de confirmación para reemplazar site winner */}
      {confirmModal.open && (
        <div className="confirm-modal-overlay" onClick={handleCancelReplace}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>⚠️ Site Winner Existente</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Ya existe un site winner del sitio <strong>{confirmModal.product?.sitio}</strong> para este producto maestro:</p>
              <div className="existing-winner-info">
                <strong>Producto actual:</strong>
                <div className="product-preview">
                  {confirmModal.existingWinner?.imagen && (
                    <img
                      src={confirmModal.existingWinner.imagen}
                      alt="Producto actual"
                      className="product-preview-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <p>{confirmModal.existingWinner?.title || `ID: ${confirmModal.existingWinner?.id_catalogo}`}</p>
                </div>
              </div>
              <div className="new-product-info">
                <strong>Nuevo producto:</strong>
                <div className="product-preview">
                  {confirmModal.product?.imagen && (
                    <img
                      src={confirmModal.product.imagen}
                      alt="Nuevo producto"
                      className="product-preview-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <p>{confirmModal.product?.title}</p>
                </div>
              </div>
              <p className="confirm-question">¿Deseas reemplazar el site winner actual con el nuevo producto?</p>
            </div>
            <div className="confirm-modal-actions">
              <button className="btn-cancel" onClick={handleCancelReplace}>
                Cancelar
              </button>
              <button className="btn-confirm" onClick={handleConfirmReplace}>
                Reemplazar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para actualizar maestro */}
      {uploadModal.open && (
        <div className="confirm-modal-overlay" onClick={() => setUploadModal({ open: false })}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>Actualizar Maestro</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Se actualizara la tabla maestro con los datos del archivo Excel:</p>
              <ul style={{ textAlign: 'left', marginTop: '10px', marginBottom: '10px' }}>
                <li>Productos existentes seran actualizados (matcheos preservados)</li>
                <li>Productos nuevos seran agregados</li>
                <li>Productos que no esten en el Excel seran eliminados</li>
              </ul>
              <p className="confirm-question">¿Deseas continuar?</p>
            </div>
            <div className="confirm-modal-actions">
              <button className="btn-cancel" onClick={() => setUploadModal({ open: false })}>
                Cancelar
              </button>
              <button className="btn-confirm" onClick={handleUploadMaestroConfirm}>
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para bulk delete de matches */}
      {bulkDeleteModal.open && (
        <div className="confirm-modal-overlay" onClick={handleBulkDeleteCancel}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>Eliminar matches seleccionados</h3>
            </div>
            <div className="confirm-modal-body">
              <p>
                Vas a eliminar <strong>{bulkDeleteModal.count}</strong> match{bulkDeleteModal.count !== 1 ? 'es' : ''} del producto maestro:
              </p>
              <p style={{ marginTop: '8px', fontWeight: 600 }}>{selectedMaestroProduct?.nombre}</p>
              <p className="confirm-question" style={{ marginTop: '12px' }}>Esta acción no se puede deshacer. ¿Continuar?</p>
            </div>
            <div className="confirm-modal-actions">
              <button className="btn-cancel" onClick={handleBulkDeleteCancel}>
                Cancelar
              </button>
              <button className="btn-confirm" onClick={handleBulkDeleteConfirm}>
                Eliminar {bulkDeleteModal.count}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProgressToast
        isOpen={exportToast.open}
        message={exportToast.message}
        progress={exportToast.progress}
        isComplete={exportToast.isComplete}
        totalRows={exportToast.totalRows}
        onClose={() => setExportToast(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
}

export default App;
